"use client";

import type { ComputerAutomationStatus } from "@praxa/contracts";
import { MonitorCog } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "~/components/ui/button";

import { computerBridge } from "./computerBridge";

export interface ComputerControlBannerViewProps {
  readonly state: ComputerAutomationStatus;
  readonly stopping: boolean;
  readonly error: string | null;
  readonly onStop: () => void;
}

const expirationLabel = (expiresAt: string | null): string => {
  if (expiresAt === null) return "briefly";
  const date = new Date(expiresAt);
  return Number.isNaN(date.getTime())
    ? "briefly"
    : `until ${date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
};

export function ComputerControlBannerView({
  state,
  stopping,
  error,
  onStop,
}: ComputerControlBannerViewProps) {
  if (state.control.state !== "granted") return null;
  const shortcut = state.platform === "darwin" ? "⌘⇧Esc" : "Ctrl+Shift+Esc";

  return (
    <aside
      aria-live="polite"
      className="pointer-events-auto fixed top-12 left-1/2 z-[100] flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-3 rounded-lg border border-border bg-background px-3 py-2 text-foreground shadow-lg"
      role="status"
    >
      <MonitorCog aria-hidden="true" className="size-4 shrink-0 text-amber-600" />
      <div className="min-w-0">
        <p className="text-sm font-medium">
          Praxa has temporary computer control {expirationLabel(state.control.expiresAt)}
        </p>
        <p className="text-xs text-muted-foreground">
          {error ?? `${shortcut} also stops control immediately.`}
        </p>
      </div>
      <Button
        aria-label="Stop Praxa computer control"
        disabled={stopping}
        onClick={onStop}
        size="sm"
        variant="destructive-outline"
      >
        {stopping ? "Stopping…" : error ? "Try again" : "Stop"}
      </Button>
    </aside>
  );
}

export function ComputerControlBanner() {
  const [state, setState] = useState<ComputerAutomationStatus | null>(null);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const bridge = computerBridge;
    if (!bridge) return;
    let active = true;
    void bridge.getStatus().then((next) => {
      if (active) setState(next);
    });
    const unsubscribe = bridge.onStateChange((next) => {
      if (!active) return;
      setState(next);
      if (next.control.state !== "granted") setError(null);
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const stop = () => {
    const bridge = computerBridge;
    if (!bridge || stopping) return;
    setStopping(true);
    setError(null);
    void bridge.releaseControl().then(
      (next) => {
        setState(next);
        setStopping(false);
      },
      () => {
        setError("Control could not be stopped. Use the emergency shortcut.");
        setStopping(false);
      },
    );
  };

  return state ? (
    <ComputerControlBannerView error={error} onStop={stop} state={state} stopping={stopping} />
  ) : null;
}
